import type { UserPreferencesNotificationsAction } from "./user-preferences-notifications.type";
import type {
  NotificationsChannel,
  NotificationsReferential,
} from "@/entities/referential/referential-notifications";
import type {
  NotificationChannels,
  NotificationKey,
  SettingsNotifications,
} from "@/entities/settings";

export const getDefaultSettings = (
  notifications: NotificationsReferential[]
): SettingsNotifications | undefined => {
  if (!notifications.length) return undefined;

  return notifications.reduce<SettingsNotifications>(
    (acc, notification) => ({
      ...acc,
      [notification.slug]: buildNotificationChannels(notification.channels),
    }),
    {} as SettingsNotifications
  );
};

export const buildNotificationChannels = (
  channels: NotificationsChannel[]
): NotificationChannels => {
  return channels.reduce<NotificationChannels>(
    (acc, channel) => ({ ...acc, [channel.name]: channel.default }),
    { broadcast: false, database: false, mail: false }
  );
};

export const getSettingChannelsFromAction = (
  action: UserPreferencesNotificationsAction,
  defaultChannels?: NotificationChannels
): NotificationChannels => {
  if (action === "activate") {
    return { broadcast: true, database: true, mail: true };
  }

  if (action === "disable") {
    return { broadcast: false, database: false, mail: false };
  }

  return defaultChannels ?? { broadcast: false, database: false, mail: false };
};

export const getSettingChannelFromAction = (
  action: UserPreferencesNotificationsAction,
  defaultValue?: boolean
): boolean => {
  if (action === "activate") return true;
  if (action === "disable") return false;
  return defaultValue ?? false;
};

export const getSettingsDiffCount = (
  source?: SettingsNotifications,
  target?: SettingsNotifications
): number => {
  if (!source || !target) return 0;

  const keys = Object.keys(source) as NotificationKey[];

  return keys.reduce(
    (count, key) =>
      count + getSettingChannelsDiffCount(source[key], target[key]),
    0
  );
};

export const getSettingChannelsDiffCount = (
  source: NotificationChannels,
  target: NotificationChannels
): number => {
  return (
    Number(source.database !== target.database) +
    Number(source.broadcast !== target.broadcast) +
    Number(source.mail !== target.mail)
  );
};
